/* eslint-disable no-comments/disallowComments */
import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';

type RTextSize = 'xs' | 'sm' | 'md' | 'xl';
type RTextWeight = 'regular' | 'medium' | 'semibold' | 'bold';
type RTextTone = 'primary' | 'secondary' | 'error' | 'warning' | 'overlay' | 'accent' | 'inherit';
type RTextAlign = 'left' | 'center' | 'right';
type RTextComponent = 'span' | 'p' | 'div';
type RTextLineHeight = 'normal' | 'relaxed';

interface RTextProps {
    children: React.ReactNode;
    /** Visual size: `xs` (11px, for tiny captions/badges/metadata), `sm` (13px), `md` (14px, default), or `xl` (24px, for standout numbers/figures). */
    size?: RTextSize;
    /** Font weight: `regular` (400, default), `medium` (500), `semibold` (600), or `bold` (700). */
    weight?: RTextWeight;
    /** Color tone: `primary` (darkNavy, default), `secondary` (mediumGrey), `error` (mediumRed), `warning` (warning amber/orange for partial/incomplete states), `overlay` (white, for text on colored backgrounds), `accent` (turquoise), or `inherit` (inherits color from parent, for cases like message bubbles where the parent controls text color). */
    tone?: RTextTone;
    /** Text alignment within the element. */
    align?: RTextAlign;
    /**
     * HTML element to render.
     * - `span` (default): inline text within a sentence or content flow.
     * - `p`: standalone paragraph or body text — carries semantic paragraph meaning.
     * - `div`: block-level text that isn't a paragraph (labels, values, table cells, layout blocks).
     */
    component?: RTextComponent;
    /** Line height: `normal` (inherited, default) or `relaxed` (1.5, for multi-line body text). */
    lineHeight?: RTextLineHeight;
    /** If true, clips overflowing text to a single line with an ellipsis. */
    truncate?: boolean;
    /** If true, preserves whitespace and line breaks in the text content (for user-generated content). */
    preserveWhitespace?: boolean;
    /** If true, renders the text in italics (e.g. loading placeholders, tentative copy). */
    italic?: boolean;
    className?: string;
}

const SIZE_PX: Record<RTextSize, number> = { xs: 11, sm: 13, md: 14, xl: 24 };
const WEIGHT_VALUE: Record<RTextWeight, number> = { regular: 400, medium: 500, semibold: 600, bold: 700 };
const TONE_COLOR: Record<RTextTone, string> = {
    primary: Colors.darkNavy,
    secondary: Colors.mediumGrey,
    error: Colors.mediumRed,
    warning: Colors.warning,
    overlay: Colors.white,
    accent: Colors.turquoise,
    inherit: 'inherit',
};
const LINE_HEIGHT_VALUE: Record<RTextLineHeight, number | undefined> = { normal: undefined, relaxed: 1.5 };

type TextStyleProps = {
    $size: RTextSize;
    $weight: RTextWeight;
    $tone: RTextTone;
    $align?: RTextAlign;
    $lineHeight: RTextLineHeight;
    $truncate: boolean;
    $preserveWhitespace: boolean;
    $italic: boolean;
};

const textStyles = ({
    $size,
    $weight,
    $tone,
    $align,
    $lineHeight,
    $truncate,
    $preserveWhitespace,
    $italic,
}: TextStyleProps) => ({
    fontSize: SIZE_PX[$size],
    fontWeight: WEIGHT_VALUE[$weight],
    color: TONE_COLOR[$tone],
    textAlign: $align,
    lineHeight: LINE_HEIGHT_VALUE[$lineHeight],
    fontStyle: $italic ? ('italic' as const) : undefined,
    margin: 0,
    ...($truncate && {
        overflow: 'hidden' as const,
        textOverflow: 'ellipsis' as const,
        whiteSpace: 'nowrap' as const,
    }),
    ...($preserveWhitespace && !$truncate && { whiteSpace: 'pre-wrap' as const }),
});

const shouldForwardTextProp = (prop: string) =>
    !['$size', '$weight', '$tone', '$align', '$lineHeight', '$truncate', '$preserveWhitespace', '$italic'].includes(
        prop,
    );

const SpanText = styled('span', { shouldForwardProp: (p) => shouldForwardTextProp(p as string) })<TextStyleProps>(
    textStyles,
);
const PText = styled('p', { shouldForwardProp: (p) => shouldForwardTextProp(p as string) })<TextStyleProps>(textStyles);
const DivText = styled('div', { shouldForwardProp: (p) => shouldForwardTextProp(p as string) })<TextStyleProps>(
    textStyles,
);

const COMPONENTS = { span: SpanText, p: PText, div: DivText };

export function RText({
    children,
    size = 'md',
    weight = 'regular',
    tone = 'primary',
    align,
    component = 'span',
    lineHeight = 'normal',
    truncate = false,
    preserveWhitespace = false,
    italic = false,
    className,
}: RTextProps) {
    const Component = COMPONENTS[component];
    return (
        <Component
            $size={size}
            $weight={weight}
            $tone={tone}
            $align={align}
            $lineHeight={lineHeight}
            $truncate={truncate}
            $preserveWhitespace={preserveWhitespace}
            $italic={italic}
            className={className}
        >
            {children}
        </Component>
    );
}
/* eslint-enable no-comments/disallowComments */
